'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { Trophy, Target, TrendingUp, Users } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';
import { getUpcomingFixtures, getFlag, type LiveFixture } from '@/lib/api-football';


// ─── Types ───────────────────────────────────────────────────────────────────

interface Prediction {
  id: string;
  user_name: string;
  flag: string;
  country: string;
  fixture_id: number;
  home_team: string;
  away_team: string;
  home_flag: string;
  away_flag: string;
  match_date: string | null;
  prediction: '1' | 'X' | '2';
  actual_result: '1' | 'X' | '2' | null;
  is_correct: boolean | null;
  created_at: string;
}

interface LeaderboardEntry {
  user_name: string;
  flag: string;
  country: string;
  total: number;
  correct: number;
  accuracy: number;
}

interface UpcomingFixture {
  fixture_id: number;
  home_team: string;
  away_team: string;
  home_flag: string;
  away_flag: string;
  match_date: string;
  status: string;
}

// ─── Mock upcoming fixtures (World Cup 2026 preview) ─────────────────────────

// remove UPCOMING_FIXTURES

const VOTE_LABELS: Record<string, string> = { '1': 'Home Win', 'X': 'Draw', '2': 'Away Win' };
const VOTE_LABELS_ES: Record<string, string> = { '1': 'Local', 'X': 'Empate', '2': 'Visitante' };

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatMatchDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function buildLeaderboard(predictions: Prediction[]): LeaderboardEntry[] {
  const map: Record<string, LeaderboardEntry> = {};
  for (const p of predictions) {
    if (!map[p.user_name]) {
      map[p.user_name] = { user_name: p.user_name, flag: p.flag, country: p.country, total: 0, correct: 0, accuracy: 0 };
    }
    if (p.actual_result !== null) {
      map[p.user_name].total += 1;
      if (p.is_correct) map[p.user_name].correct += 1;
    }
  }
  return Object.values(map)
    .map((e) => ({ ...e, accuracy: e.total > 0 ? Math.round((e.correct / e.total) * 100) : 0 }))
    .filter((e) => e.total > 0)
    .sort((a, b) => b.correct - a.correct || b.accuracy - a.accuracy);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function VoteBar({ fixture, myVote, allVotes, onVote, lang }: {
  fixture: UpcomingFixture;
  myVote: '1' | 'X' | '2' | null;
  allVotes: Prediction[];
  onVote: (fixtureId: number, vote: '1' | 'X' | '2') => void;
  lang: string;
}) {
  const labels = lang === 'es' ? VOTE_LABELS_ES : VOTE_LABELS;
  const relevant = allVotes.filter((p) => p.fixture_id === fixture.fixture_id);
  const counts = { '1': 0, 'X': 0, '2': 0 };
  for (const p of relevant) counts[p.prediction] += 1;
  const total = counts['1'] + counts['X'] + counts['2'];
  const pct = (k: '1' | 'X' | '2') => (total > 0 ? Math.round((counts[k] / total) * 100) : 33);

  const options: Array<{ key: '1' | 'X' | '2'; label: string; color: string; bg: string; border: string }> = [
    { key: '1', label: labels['1'], color: 'text-emerald-400', bg: 'bg-emerald-500/20', border: 'border-emerald-500/60' },
    { key: 'X', label: labels['X'], color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/60' },
    { key: '2', label: labels['2'], color: 'text-sky-400', bg: 'bg-sky-500/20', border: 'border-sky-500/60' },
  ];

  return (
    <div
      className="rounded-xl p-4 border transition-all duration-200"
      style={{ background: 'hsl(222 35% 11%)', borderColor: 'hsl(222 25% 18%)' }}
    >
      {/* Match header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xl">{fixture.home_flag}</span>
          <span className="text-sm font-semibold text-white truncate">{fixture.home_team}</span>
        </div>
        <div className="flex flex-col items-center px-2">
          <span className="text-xs font-bold text-orange-400 tracking-widest">VS</span>
          <span className="text-xs text-slate-500 mt-0.5 whitespace-nowrap">{formatMatchDate(fixture.match_date)}</span>
        </div>
        <div className="flex items-center gap-2 min-w-0 justify-end">
          <span className="text-sm font-semibold text-white truncate">{fixture.away_team}</span>
          <span className="text-xl">{fixture.away_flag}</span>
        </div>
      </div>

      {/* Vote buttons */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {options.map(({ key, label, color, bg, border }) => {
          const selected = myVote === key;
          return (
            <button
              key={key}
              onClick={() => onVote(fixture.fixture_id, key)}
              className={`flex flex-col items-center py-2.5 px-1 rounded-lg border text-xs font-bold transition-all duration-150 ${
                selected
                  ? `${bg} ${border} ${color} scale-105 shadow-lg`
                  : 'border-transparent bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white'
              }`}
            >
              <span className="text-base font-black">{key}</span>
              <span className="mt-0.5 text-[10px] font-medium">{label}</span>
            </button>
          );
        })}
      </div>

      {/* Vote distribution bar */}
      {total > 0 && (
        <div className="space-y-1.5">
          <div className="flex rounded-full overflow-hidden h-1.5">
            <div className="bg-emerald-500 transition-all duration-500" style={{ width: `${pct('1')}%` }} />
            <div className="bg-amber-500 transition-all duration-500" style={{ width: `${pct('X')}%` }} />
            <div className="bg-sky-500 transition-all duration-500" style={{ width: `${pct('2')}%` }} />
          </div>
          <div className="flex justify-between text-[10px] text-slate-500">
            <span className="text-emerald-400">{pct('1')}%</span>
            <span className="text-amber-400">{pct('X')}%</span>
            <span className="text-sky-400">{pct('2')}%</span>
          </div>
          <p className="text-center text-[10px] text-slate-600">{total} {total === 1 ? 'vote' : 'votes'}</p>
        </div>
      )}
    </div>
  );
}

function LeaderboardRow({ entry, rank, t }: { entry: LeaderboardEntry; rank: number; t: any }) {
  const isTop3 = rank <= 3;
  const medalColors = ['text-yellow-400', 'text-slate-300', 'text-amber-600'];
  const medals = ['🥇', '🥈', '🥉'];

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-150 ${
        isTop3 ? 'border' : 'border border-transparent hover:border-white/5'
      }`}
      style={isTop3 ? { background: 'hsl(222 35% 13%)', borderColor: 'hsl(222 25% 20%)' } : { background: 'hsl(222 35% 10%)' }}
    >
      <div className="w-8 text-center flex-shrink-0">
        {isTop3 ? (
          <span className="text-lg">{medals[rank - 1]}</span>
        ) : (
          <span className={`text-sm font-bold ${medalColors[0] ?? 'text-slate-500'}`} style={{ color: 'hsl(222 15% 50%)' }}>
            {rank}
          </span>
        )}
      </div>
      <span className="text-lg flex-shrink-0">{entry.flag}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white truncate">{entry.user_name}</p>
        <p className="text-xs text-slate-500">{entry.country}</p>
      </div>
      <div className="flex items-center gap-4 flex-shrink-0">
        <div className="text-center hidden sm:block">
          <p className="text-xs text-slate-500">{t.predictions.correct}</p>
          <p className="text-sm font-bold text-white">{entry.correct}/{entry.total}</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-slate-500">{t.predictions.accuracy}</p>
          <p
            className={`text-sm font-bold ${
              entry.accuracy >= 70 ? 'text-emerald-400' : entry.accuracy >= 50 ? 'text-amber-400' : 'text-slate-400'
            }`}
          >
            {entry.accuracy}%
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PredictionsPage() {
  const { language, t } = useLanguage();
  const supabase = createClient();

  const [allPredictions, setAllPredictions] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'vote' | 'leaderboard'>('vote');
  const [upcomingFixtures, setUpcomingFixtures] = useState<UpcomingFixture[]>([]);
  const [fixturesLoading, setFixturesLoading] = useState(true);

  // Fan identity (persisted in localStorage)
  const [fanName, setFanName] = useState('');
  const [fanFlag, setFanFlag] = useState('🌍');
  const [fanCountry, setFanCountry] = useState('Global');
  const [showIdentityForm, setShowIdentityForm] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [flagInput, setFlagInput] = useState('🌍');
  const [countryInput, setCountryInput] = useState('');
  const [submitting, setSubmitting] = useState<number | null>(null);

  // Load fan identity from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('noticopa_fan_identity');
      if (stored) {
        const { name, flag, country } = JSON.parse(stored);
        setFanName(name || '');
        setFanFlag(flag || '🌍');
        setFanCountry(country || 'Global');
        setNameInput(name || '');
        setFlagInput(flag || '🌍');
        setCountryInput(country || '');
      }
    } catch { /* ignore */ }
  }, []);

  // Load upcoming fixtures from API
  useEffect(() => {
    async function fetchUpcoming() {
      try {
        const data = await getUpcomingFixtures(12);
        if (data && data.length > 0) {
          const mapped: UpcomingFixture[] = data.map((fix: LiveFixture) => ({
            fixture_id: fix.fixture.id,
            home_team: fix.teams.home.name,
            away_team: fix.teams.away.name,
            home_flag: getFlag(fix.teams.home.name),
            away_flag: getFlag(fix.teams.away.name),
            match_date: fix.fixture.date,
            status: fix.fixture.status?.short ?? 'NS',
          }));
          setUpcomingFixtures(mapped);
        } else {
          // Fallback preview fixtures when API returns nothing
          setUpcomingFixtures([
            { fixture_id: 2001, home_team: 'USA', away_team: 'Mexico', home_flag: '🇺🇸', away_flag: '🇲🇽', match_date: '2026-06-15T18:00:00Z', status: 'upcoming' },
            { fixture_id: 2002, home_team: 'Brazil', away_team: 'Colombia', home_flag: '🇧🇷', away_flag: '🇨🇴', match_date: '2026-06-16T20:00:00Z', status: 'upcoming' },
            { fixture_id: 2003, home_team: 'France', away_team: 'Portugal', home_flag: '🇫🇷', away_flag: '🇵🇹', match_date: '2026-06-17T16:00:00Z', status: 'upcoming' },
            { fixture_id: 2004, home_team: 'Germany', away_team: 'Spain', home_flag: '🇩🇪', away_flag: '🇪🇸', match_date: '2026-06-18T20:00:00Z', status: 'upcoming' },
            { fixture_id: 2005, home_team: 'Argentina', away_team: 'England', home_flag: '🇦🇷', away_flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', match_date: '2026-06-19T18:00:00Z', status: 'upcoming' },
            { fixture_id: 2006, home_team: 'Japan', away_team: 'Morocco', home_flag: '🇯🇵', away_flag: '🇲🇦', match_date: '2026-06-20T14:00:00Z', status: 'upcoming' },
          ]);
        }
      } catch {
        setUpcomingFixtures([]);
      } finally {
        setFixturesLoading(false);
      }
    }
    fetchUpcoming();
  }, []);

  const saveFanIdentity = () => {
    const name = nameInput.trim() || 'Fan';
    const flag = flagInput.trim() || '🌍';
    const country = countryInput.trim() || 'Global';
    setFanName(name);
    setFanFlag(flag);
    setFanCountry(country);
    try {
      localStorage.setItem('noticopa_fan_identity', JSON.stringify({ name, flag, country }));
    } catch { /* ignore */ }
    setShowIdentityForm(false);
  };

  // Load predictions from Supabase
  const loadPredictions = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('predictions')
        .select('*')
        .order('created_at', { ascending: false });
      if (!error && data) setAllPredictions(data as Prediction[]);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    loadPredictions();
    // Realtime subscription
    const channel = supabase
      .channel('predictions_realtime')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'predictions' }, (payload) => {
        setAllPredictions((prev) => [payload.new as Prediction, ...prev]);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [loadPredictions, supabase]);

  // Get my vote for a fixture
  const getMyVote = (fixtureId: number): '1' | 'X' | '2' | null => {
    if (!fanName) return null;
    const found = allPredictions.find((p) => p.fixture_id === fixtureId && p.user_name === fanName);
    return found ? found.prediction : null;
  };

  // Submit vote
  const handleVote = async (fixtureId: number, vote: '1' | 'X' | '2') => {
    const name = fanName || 'Fan';
    const fixture = upcomingFixtures.find((f) => f.fixture_id === fixtureId);
    if (!fixture) return;

    // Prevent duplicate vote for same fixture
    const existing = allPredictions.find((p) => p.fixture_id === fixtureId && p.user_name === name);
    if (existing) return;

    setSubmitting(fixtureId);
    try {
      const { data, error } = await supabase.from('predictions').insert({
        user_name: name,
        flag: fanFlag,
        country: fanCountry,
        fixture_id: fixtureId,
        home_team: fixture.home_team,
        away_team: fixture.away_team,
        home_flag: fixture.home_flag,
        away_flag: fixture.away_flag,
        match_date: fixture.match_date,
        prediction: vote,
        actual_result: null,
        is_correct: null,
      }).select().single();
      if (!error && data) {
        setAllPredictions((prev) => [data as Prediction, ...prev]);
      }
    } catch { /* ignore */ } finally {
      setSubmitting(null);
    }
  };

  // Stats for current fan
  const myPredictions = allPredictions.filter((p) => p.user_name === fanName && p.actual_result !== null);
  const myCorrect = myPredictions.filter((p) => p.is_correct).length;
  const myAccuracy = myPredictions.length > 0 ? Math.round((myCorrect / myPredictions.length) * 100) : 0;
  const myVotedCount = allPredictions.filter((p) => p.user_name === fanName).length;

  const leaderboard = buildLeaderboard(allPredictions);
  const myRank = leaderboard.findIndex((e) => e.user_name === fanName) + 1;

  const isES = language === 'es';

  return (
    <AppLayout activeRoute="/predictions">
      {/* Page header */}
      <div className="mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Target size={24} className="text-orange-400" />
              {t.predictions.title}
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              {t.predictions.subtitle}
            </p>
          </div>

          {/* Fan identity badge */}
          <button
            onClick={() => { setShowIdentityForm(true); setNameInput(fanName); setFlagInput(fanFlag); setCountryInput(fanCountry); }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all hover:border-orange-500/50"
            style={{ background: 'hsl(222 35% 11%)', borderColor: 'hsl(222 25% 18%)' }}
          >
            <span className="text-lg">{fanFlag}</span>
            <span className="text-white font-medium">{fanName || t.predictions.setName}</span>
            <span className="text-xs text-slate-500 ml-1">{t.predictions.edit}</span>
          </button>
        </div>

        {/* Identity form modal */}
        {showIdentityForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
            <div className="w-full max-w-sm rounded-2xl border p-6 space-y-4" style={{ background: 'hsl(222 40% 10%)', borderColor: 'hsl(222 25% 20%)' }}>
              <h2 className="text-lg font-bold text-white">{t.predictions.fanIdentity}</h2>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">{t.predictions.fanName}</label>
                  <input
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    placeholder={t.predictions.yourName}
                    maxLength={30}
                    className="w-full px-3 py-2 rounded-lg text-sm text-white border outline-none focus:border-orange-500/60"
                    style={{ background: 'hsl(222 35% 14%)', borderColor: 'hsl(222 25% 22%)' }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">{t.predictions.flagEmoji}</label>
                    <input
                      value={flagInput}
                      onChange={(e) => setFlagInput(e.target.value)}
                      placeholder="🌍"
                      maxLength={4}
                      className="w-full px-3 py-2 rounded-lg text-sm text-white border outline-none focus:border-orange-500/60"
                      style={{ background: 'hsl(222 35% 14%)', borderColor: 'hsl(222 25% 22%)' }}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">{t.predictions.country}</label>
                    <input
                      value={countryInput}
                      onChange={(e) => setCountryInput(e.target.value)}
                      placeholder={t.predictions.yourCountry}
                      maxLength={20}
                      className="w-full px-3 py-2 rounded-lg text-sm text-white border outline-none focus:border-orange-500/60"
                      style={{ background: 'hsl(222 35% 14%)', borderColor: 'hsl(222 25% 22%)' }}
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setShowIdentityForm(false)}
                  className="flex-1 py-2 rounded-lg text-sm text-slate-400 border transition-all hover:text-white"
                  style={{ borderColor: 'hsl(222 25% 20%)' }}
                >
                  {t.predictions.cancel}
                </button>
                <button
                  onClick={saveFanIdentity}
                  className="flex-1 py-2 rounded-lg text-sm font-semibold text-white bg-orange-500 hover:bg-orange-400 transition-all"
                >
                  {t.predictions.save}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats row */}
      {fanName && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { icon: Target, label: t.predictions.predictions, value: myVotedCount, color: 'text-orange-400' },
            { icon: Trophy, label: t.predictions.correct, value: myCorrect, color: 'text-emerald-400' },
            { icon: TrendingUp, label: t.predictions.accuracy, value: `${myAccuracy}%`, color: myAccuracy >= 70 ? 'text-emerald-400' : myAccuracy >= 50 ? 'text-amber-400' : 'text-slate-400' },
            { icon: Users, label: t.predictions.rank, value: myRank > 0 ? `#${myRank}` : '—', color: 'text-sky-400' },
          ].map(({ icon: Icon, label, value, color }) => (
            <div
              key={label}
              className="rounded-xl p-4 border flex items-center gap-3"
              style={{ background: 'hsl(222 35% 11%)', borderColor: 'hsl(222 25% 18%)' }}
            >
              <Icon size={20} className={`${color} flex-shrink-0`} />
              <div>
                <p className="text-xs text-slate-500">{label}</p>
                <p className={`text-xl font-bold ${color}`}>{value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-5 p-1 rounded-xl w-fit" style={{ background: 'hsl(222 35% 11%)' }}>
        {(['vote', 'leaderboard'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
              activeTab === tab ? 'bg-orange-500 text-white shadow' : 'text-slate-400 hover:text-white'
            }`}
          >
            {tab === 'vote' ? t.predictions.vote : t.predictions.leaderboard}
          </button>
        ))}
      </div>

      {/* Vote tab */}
      {activeTab === 'vote' && (
        <div className="space-y-4">
          {!fanName && (
            <div
              className="rounded-xl p-4 border flex items-center gap-3 mb-2"
              style={{ background: 'hsl(222 35% 11%)', borderColor: 'hsl(222 25% 18%)' }}
            >
              <span className="text-2xl">👆</span>
              <p className="text-sm text-slate-300">
                {t.predictions.setNamePrompt}
              </p>
            </div>
          )}

          {loading || fixturesLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {upcomingFixtures.map((fixture) => {
                const myVote = getMyVote(fixture.fixture_id);
                const isSubmitting = submitting === fixture.fixture_id;
                return (
                  <div key={fixture.fixture_id} className={`relative ${isSubmitting ? 'opacity-60 pointer-events-none' : ''}`}>
                    <VoteBar
                      fixture={fixture}
                      myVote={myVote}
                      allVotes={allPredictions}
                      onVote={handleVote}
                      lang={language}
                    />
                    {myVote && (
                      <div className="absolute top-3 right-3">
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-400 border border-orange-500/30">
                          {t.predictions.voted} {myVote}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Leaderboard tab */}
      {activeTab === 'leaderboard' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <Trophy size={16} className="text-yellow-400" />
              {t.predictions.fanLeaderboard}
            </h2>
            <span className="text-xs text-slate-500">{leaderboard.length} {t.predictions.fans}</span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <Trophy size={40} className="mx-auto mb-3 opacity-30" />
              <p>{t.predictions.noCompleted}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Header row */}
              <div className="flex items-center gap-3 px-4 py-2 text-xs text-slate-500 font-medium">
                <div className="w-8 text-center">#</div>
                <div className="w-6" />
                <div className="flex-1">{t.predictions.fans}</div>
                <div className="hidden sm:block w-20 text-center">{t.predictions.correct}</div>
                <div className="w-20 text-center">{t.predictions.accuracy}</div>
              </div>
              {leaderboard.map((entry, idx) => (
                <div
                  key={entry.user_name}
                  className={fanName === entry.user_name ? 'ring-1 ring-orange-500/40 rounded-lg' : ''}
                >
                  <LeaderboardRow entry={entry} rank={idx + 1} t={t} />
                </div>
              ))}
            </div>
          )}

          {/* Legend */}
          <div className="mt-6 p-4 rounded-xl border" style={{ background: 'hsl(222 35% 10%)', borderColor: 'hsl(222 25% 16%)' }}>
            <p className="text-xs text-slate-500 font-medium mb-2">{t.predictions.howItWorks}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-slate-400">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-[10px]">1</span>
                <span>{t.predictions.homeWin}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center font-bold text-[10px]">X</span>
                <span>{t.predictions.draw}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-sky-500/20 text-sky-400 flex items-center justify-center font-bold text-[10px]">2</span>
                <span>{t.predictions.awayWin}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
